<template>
  <el-row class="d-flex">
    <el-button
      v-if="showNewConfigurationButton"
      class="ml-auto"
      type="primary"
      @click="showNewConfigurationModal = true"
    >
      <h4>{{ $t('new') }}</h4>
    </el-button>
  </el-row>
  <el-row
    v-for="(item, index) in localConfigurations"
    :key="index"
    class="d-block mt-1"
  >
    <api-connections-configuration-card
      :configuration="item"
      :configuration-history="apiConnectionConfigurationHistory"
      @update="(payload: ApiConnectionConfiguration) => handleConfigurationUpdate(index, payload)"
      @delete="handleConfigurationDelete(index)"
      @save="handleConfigurationSave(index)"
      @fetch-history="fetchApiConnectionConfigurationHistory(item.apiName!)"
    />
  </el-row>
  <new-api-connections-configuration-modal
    :model-value="showNewConfigurationModal"
    :existing-api-names-values="remainingApiNames"
    @close="showNewConfigurationModal = false"
    @confirm="handleConfigurationCreate"
  />
  <base-confirmation-modal
    v-model="showDeleteConfigurationModal"
    :feedback-text="$t('configurationDeleted')"
    @confirm="deleteConfiguration"
    @close="
      {
        apiNameToBeDeleted = null;
        showDeleteConfigurationModal = false;
      }
    "
  >
    <template #content>
      <p>{{ $t('confirmDeleteConfiguration') }}</p>
      <p class="mt-2">
        <strong>{{ $t('confirmDeleteConfigurationNote') }}</strong>
      </p>
    </template>
  </base-confirmation-modal>
</template>
<script lang="ts">
import { ElButton, ElMessage, ElRow } from 'element-plus';
import NewApiConnectionsConfigurationModal from './components/NewApiConnectionsConfigurationModal.vue';
import { computed, ComputedRef, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import i18n from '@/i18n';
import {
  ApiConnectionConfiguration,
  ApiName
} from '@etp/etp-gain-bff-client/axios';
import BaseConfirmationModal from '@/components/modals/BaseConfirmationModal.vue';
import ApiConnectionsConfigurationCard from './components/ApiConnectionsConfigurationCard.vue';
import { useRouter } from 'vue-router';

export default {
  name: 'EngagementApiConnectionsConfiguration',
  components: {
    ElButton,
    ElRow,
    BaseConfirmationModal,
    NewApiConnectionsConfigurationModal,
    ApiConnectionsConfigurationCard
  },
  setup() {
    let engagementId = '';
    const store = useStore();
    const router = useRouter();

    const localConfigurations = ref(new Array<ApiConnectionConfiguration>());
    const showNewConfigurationModal = ref(false);
    const showDeleteConfigurationModal = ref(false);
    const apiNameToBeDeleted = ref<number | undefined | null>(null);

    const remainingApiNames = computed(() => {
      return Object.values(ApiName).filter(
        apiName =>
          !localConfigurations.value
            .map(el => el.apiName ?? null)
            .filter(el => el !== null)
            .includes(apiName)
      );
    });

    const showNewConfigurationButton = computed(() => {
      return localConfigurations.value.length < Object.keys(ApiName).length;
    });

    const apiConnectionConfigurationHistory: ComputedRef<
      Array<ApiConnectionConfiguration>
    > = computed(
      () => store.getters['engagements/getApiConnectionConfigurationHistory']
    );

    const handleConfigurationCreate = async (name: ApiName) => {
      localConfigurations.value.push({
        apiName: name
      } as ApiConnectionConfiguration);
      showNewConfigurationModal.value = false;
    };

    const handleConfigurationDelete = async (index: number) => {
      const configuration = localConfigurations.value[index];
      if (configuration.version === undefined) {
        localConfigurations.value.splice(index, 1);
      } else {
        showDeleteConfigurationModal.value = true;
        apiNameToBeDeleted.value = configuration.apiName ?? null;
      }
    };

    const deleteConfiguration = async () => {
      await store.dispatch('engagements/deleteApiConnectionConfiguration', {
        engagementId,
        apiName: apiNameToBeDeleted.value
      });
      apiNameToBeDeleted.value = null;
      showDeleteConfigurationModal.value = false;
      await fetchConfigurations();
    };

    const handleConfigurationUpdate = async (
      index: number,
      payload: ApiConnectionConfiguration
    ) => {
      localConfigurations.value[index].enabled = payload.enabled;
      localConfigurations.value[index].clientId = payload.clientId;
      localConfigurations.value[index].clientSecretKeyName =
        payload.clientSecretKeyName;
      localConfigurations.value[index].tokenUrl = payload.tokenUrl;
      localConfigurations.value[index].scope = payload.scope;
      localConfigurations.value[index].grantType = payload.grantType;
      localConfigurations.value[index].responseType = payload.responseType;
      localConfigurations.value[index].dataEndpoint = payload.dataEndpoint;
    };

    const handleConfigurationSave = async (index: number) => {
      const statusCode = await store.dispatch(
        'engagements/createOrUpdateApiConnectionConfiguration',
        {
          engagementId,
          apiName: localConfigurations.value[index].apiName,
          configuration: localConfigurations.value[index]
        }
      );
      ElMessage({
        message: i18n.global.t(
          statusCode > 300
            ? 'apiConnectionConfigurationSaveFailure'
            : 'apiConnectionConfigurationSaveSuccess'
        )
      });
      await fetchConfigurations();
    };

    const fetchConfigurations = async () => {
      await store.dispatch(
        'engagements/fetchApiConnectionConfigurations',
        engagementId
      );
      localConfigurations.value = [
        ...store.getters['engagements/getApiConnectionConfigurations']
      ];
    };

    const fetchApiConnectionConfigurationHistory = async (apiName: ApiName) => {
      await store.dispatch(
        'engagements/fetchApiConnectionConfigurationHistory',
        {
          engagementId,
          apiName
        }
      );
    };

    onMounted(async () => {
      engagementId = router.currentRoute.value.params.engagementId as string;
      await fetchConfigurations();
    });

    return {
      handleConfigurationCreate,
      handleConfigurationDelete,
      handleConfigurationUpdate,
      handleConfigurationSave,
      localConfigurations,
      showNewConfigurationModal,
      fetchApiConnectionConfigurationHistory,
      apiConnectionConfigurationHistory,
      showDeleteConfigurationModal,
      deleteConfiguration,
      apiNameToBeDeleted,
      showNewConfigurationButton,
      remainingApiNames
    };
  }
};
</script>
